/**
 * Monte Carlo Simulation Engine
 *
 * Browser-side Monte Carlo simulations (10,000 iterations) producing
 * probability distributions for loss scenarios and aggregate annual exposure.
 * Uses seeded PRNG for reproducibility.
 */

// ── Seeded PRNG (xorshift128) ───────────────────────────────────────────────
class SeededRNG {
  constructor(seed = 42) {
    this.s = [seed, seed * 2 + 1, seed * 3 + 7, seed * 5 + 13]
  }

  next() {
    const s = this.s
    let t = s[3]
    t ^= t << 11
    t ^= t >>> 8
    s[3] = s[2]; s[2] = s[1]; s[1] = s[0]
    t ^= s[0]
    t ^= s[0] >>> 19
    s[0] = t
    return (t >>> 0) / 4294967296
  }
}

// ── Distribution Samplers ───────────────────────────────────────────────────

/**
 * Sample from standard normal using Box-Muller transform.
 */
function sampleNormal(rng) {
  const u1 = rng.next()
  const u2 = rng.next()
  return Math.sqrt(-2 * Math.log(Math.max(u1, 1e-10))) * Math.cos(2 * Math.PI * u2)
}

/**
 * Sample from a Beta distribution using Jöhnk's algorithm.
 * Used for occurrence probability (bounded 0-1).
 */
function sampleBeta(alpha, beta, rng) {
  if (alpha <= 0 || beta <= 0) return 0.5

  // For alpha,beta > 1, use Cheng's algorithm (more efficient)
  if (alpha > 1 && beta > 1) {
    const a = Math.min(alpha, beta)
    const b = Math.max(alpha, beta)
    const lambda = Math.sqrt((2 * a * b - a - b) / (a * b - 2))
    while (true) {
      const u1 = rng.next()
      const u2 = rng.next()
      const v = lambda * Math.log(u1 / (1 - u1))
      const w = a * Math.exp(v)
      const z = u1 * u1 * u2
      const r = (a + b) * Math.log((a + b) / (a + w)) + a * v - Math.log(4)
      if (r >= Math.log(z)) {
        return alpha <= beta ? w / (a + w) : a / (a + w)
      }
    }
  }

  // Jöhnk's algorithm for small alpha/beta
  while (true) {
    const u1 = rng.next()
    const u2 = rng.next()
    const x = Math.pow(u1, 1 / alpha)
    const y = Math.pow(u2, 1 / beta)
    if (x + y <= 1) {
      return x / (x + y)
    }
  }
}

/**
 * Sample from a LogNormal distribution.
 * Used for financial impact (right-skewed).
 */
function sampleLogNormal(mu, sigma, rng) {
  return Math.exp(mu + sigma * sampleNormal(rng))
}

/**
 * Sample from a Gamma distribution using Marsaglia and Tsang's method.
 */
function sampleGamma(shape, scale, rng) {
  if (shape < 1) {
    return sampleGamma(shape + 1, scale, rng) * Math.pow(rng.next(), 1 / shape)
  }

  const d = shape - 1 / 3
  const c = 1 / Math.sqrt(9 * d)

  while (true) {
    let x, v
    do {
      x = sampleNormal(rng)
      v = 1 + c * x
    } while (v <= 0)

    v = v * v * v
    const u = rng.next()

    if (u < 1 - 0.0331 * x * x * x * x) return d * v * scale
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v * scale
  }
}

// ── Scenario Correlation Matrix ─────────────────────────────────────────────
const SCENARIO_CORRELATIONS = {
  cyber_extortion: {
    business_interruption: 0.85,
    data_breach: 0.60,
    regulatory: 0.45
  },
  data_breach: {
    regulatory: 0.70,
    third_party: 0.30,
    financial_theft: 0.20
  },
  financial_theft: {
    insider_threat: 0.25
  }
}

// ── Percentile Calculator ───────────────────────────────────────────────────
function calculatePercentile(sorted, p) {
  const idx = Math.floor(sorted.length * p)
  return sorted[Math.min(idx, sorted.length - 1)]
}

function calculateStats(values) {
  const sorted = [...values].sort((a, b) => a - b)
  const n = sorted.length
  const mean = values.reduce((a, b) => a + b, 0) / n
  const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / n
  const stdDev = Math.sqrt(variance)
  const skewness = n > 2 ? values.reduce((sum, v) => sum + ((v - mean) / stdDev) ** 3, 0) / n : 0

  return {
    percentiles: {
      p5: Math.round(calculatePercentile(sorted, 0.05)),
      p10: Math.round(calculatePercentile(sorted, 0.10)),
      p25: Math.round(calculatePercentile(sorted, 0.25)),
      p50: Math.round(calculatePercentile(sorted, 0.50)),
      p75: Math.round(calculatePercentile(sorted, 0.75)),
      p90: Math.round(calculatePercentile(sorted, 0.90)),
      p95: Math.round(calculatePercentile(sorted, 0.95)),
      p99: Math.round(calculatePercentile(sorted, 0.99))
    },
    mean: Math.round(mean),
    stdDev: Math.round(stdDev),
    skewness: Math.round(skewness * 10) / 10,
    min: Math.round(sorted[0]),
    max: Math.round(sorted[n - 1])
  }
}

/**
 * Build histogram data from simulation values.
 */
function buildHistogram(values, bins = 30) {
  const nonZero = values.filter(v => v > 0)
  const zeroCount = values.length - nonZero.length
  const histogram = [{ binStart: 0, binEnd: 0, count: zeroCount, label: 'No incident' }]

  if (nonZero.length === 0) return histogram

  const sorted = [...nonZero].sort((a, b) => a - b)
  const maxVal = sorted[sorted.length - 1]
  const binWidth = maxVal / (bins - 1)

  for (let i = 0; i < bins - 1; i++) {
    const binStart = i * binWidth + 1
    const binEnd = (i + 1) * binWidth
    const count = nonZero.filter(v => v >= binStart && v < binEnd).length
    const startLabel = binStart >= 1000000 ? `$${(binStart / 1000000).toFixed(1)}M` : `$${Math.round(binStart / 1000)}K`
    const endLabel = binEnd >= 1000000 ? `$${(binEnd / 1000000).toFixed(1)}M` : `$${Math.round(binEnd / 1000)}K`
    histogram.push({ binStart: Math.round(binStart), binEnd: Math.round(binEnd), count, label: `${startLabel}-${endLabel}` })
  }

  return histogram
}

/**
 * Run Monte Carlo simulation for a single loss scenario.
 *
 * @param {Object} scenarioParams - { scenarioId, scenarioName, probability, grossLoss, insuranceOffset, calibration }
 * @param {number} iterations - Number of simulation runs
 * @returns {Object} Distribution results
 */
export function simulateScenario(scenarioParams, iterations = 10000) {
  const {
    scenarioId,
    scenarioName,
    probability = 0.15,
    grossLoss = 200000,
    insuranceOffset = 0,
    calibration = {}
  } = scenarioParams

  const rng = new SeededRNG(scenarioId ? hashString(scenarioId) : 42)

  // Calibrate parameters
  const calProb = Math.min(0.95, Math.max(0.01, probability * (calibration.probabilityModifier || 1)))
  const calLoss = grossLoss * (calibration.costModifier || 1)

  // Beta distribution for occurrence
  const concentration = 30
  const alpha = calProb * concentration
  const beta = (1 - calProb) * concentration

  // LogNormal for loss magnitude
  const medianLoss = calLoss
  const p95Ratio = 2.5 // 95th percentile is 2.5x median
  const mu = Math.log(Math.max(medianLoss, 100))
  const sigma = Math.log(p95Ratio) / 1.645

  const grossValues = []
  const netValues = []
  let occurrences = 0

  for (let i = 0; i < iterations; i++) {
    const occurs = sampleBeta(alpha, beta, rng) > 0.5
    if (occurs) {
      occurrences++
      const loss = Math.max(0, sampleLogNormal(mu, sigma, rng))
      grossValues.push(loss)
      netValues.push(Math.max(0, loss - insuranceOffset))
    } else {
      grossValues.push(0)
      netValues.push(0)
    }
  }

  const grossStats = calculateStats(grossValues)
  const netStats = calculateStats(netValues)
  const occurrenceRate = Math.round((occurrences / iterations) * 100) / 100

  // Conditional distribution: loss given the incident actually occurs
  const givenOccurrence = grossValues.filter(v => v > 0)
  const givenOccurrenceStats = givenOccurrence.length > 0 ? calculateStats(givenOccurrence) : null

  return {
    scenarioId,
    scenarioName,
    iterations,
    distribution: grossStats,
    givenOccurrence: givenOccurrenceStats,
    occurrenceRate,
    histogram: buildHistogram(grossValues),
    grossExposure: { p50: grossStats.percentiles.p50, p90: grossStats.percentiles.p90, p95: grossStats.percentiles.p95 },
    netExposure: { p50: netStats.percentiles.p50, p90: netStats.percentiles.p90, p95: netStats.percentiles.p95 },
    executiveSummary: `There is a ${Math.round(occurrenceRate * 100)}% annual probability of ${scenarioName.toLowerCase()}. ${occurrenceRate > 0.05 ? `If one occurs, the median cost is $${formatCurrency(givenOccurrenceStats?.percentiles?.p50 || grossStats.percentiles.p50)} with a 10% chance of exceeding $${formatCurrency(givenOccurrenceStats?.percentiles?.p90 || grossStats.percentiles.p90)}.` : 'This scenario is unlikely given current controls.'}`
  }
}

/**
 * Run aggregate Monte Carlo across all scenarios.
 */
export function simulateAggregateExposure(scenarioResults, dynamicCalibration = {}, impactData = {}, iterations = 10000) {
  const rng = new SeededRNG(12345)
  const coverageLimit = impactData.insurance_coverage_limit || impactData.insuranceCoverageLimit || 1000000
  const deductible = impactData.deductible || impactData.client_retention || 10000
  const hasInsurance = impactData.has_cyber_insurance !== false

  // Per-scenario sublimits (from the Insurance Policy Review). When present
  // the effective coverage for a given scenario is min(aggregate, sublimit).
  // We resolve the sublimit by best-effort name matching against scenarioId.
  const sublimitMap = impactData.insuranceSublimits || {}
  const resolveSublimit = (scenarioId) => {
    if (!scenarioId || !sublimitMap || Object.keys(sublimitMap).length === 0) return null
    const sid = String(scenarioId).toLowerCase().replace(/[^a-z0-9]+/g, '_')
    for (const [k, v] of Object.entries(sublimitMap)) {
      const key = String(k).toLowerCase().replace(/[^a-z0-9]+/g, '_')
      const num = typeof v === 'number' ? v : parseFloat(v)
      if (!Number.isFinite(num) || num <= 0) continue
      if (sid === key || sid.includes(key) || key.includes(sid)) return num
      // Common mapping aliases
      if ((sid.includes('extort') || sid.includes('ransom')) && (key.includes('extort') || key.includes('ransom'))) return num
      if (sid.includes('breach') && (key.includes('breach') || key.includes('privacy'))) return num
      if (sid.includes('social') && (key.includes('social') || key.includes('fraud'))) return num
      if (sid.includes('business_interrupt') && (key.includes('bi') || key.includes('interrupt'))) return num
    }
    return null
  }

  const aggregateGross = []
  const aggregateNet = []
  let zeroIncidents = 0, oneIncident = 0, twoIncidents = 0, threeOrMore = 0

  const calibrations = dynamicCalibration?.scenarioAdjustments || {}

  for (let i = 0; i < iterations; i++) {
    let yearlyGross = 0
    let yearlyInsured = 0  // running total of recoveries (capped by aggregate)
    let incidentCount = 0

    for (const scenario of scenarioResults) {
      if (!scenario) continue
      const cal = calibrations[scenario.scenarioId] || {}
      const prob = Math.min(0.95, (scenario.probability || 0.15) * (cal.probabilityModifier || 1))

      // Check if incident occurs this iteration
      if (rng.next() < prob) {
        incidentCount++
        const medianLoss = (scenario.grossLoss || 200000) * (cal.costModifier || 1)
        const mu = Math.log(Math.max(medianLoss, 100))
        const sigma = Math.log(2.5) / 1.645
        const loss = Math.max(0, sampleLogNormal(mu, sigma, rng))

        // Apply correlations — if this scenario occurs, check correlated scenarios
        const correlations = SCENARIO_CORRELATIONS[scenario.scenarioId]
        if (correlations) {
          for (const [corrScenario, corrProb] of Object.entries(correlations)) {
            if (rng.next() < corrProb * 0.3) { // Dampen correlation effect
              const corrResult = scenarioResults.find(s => s.scenarioId === corrScenario)
              if (corrResult) {
                const corrLoss = (corrResult.grossLoss || 100000) * 0.5 // Partial correlated loss
                yearlyGross += corrLoss
              }
            }
          }
        }

        yearlyGross += loss

        // Track insured recovery per scenario, capped by sublimit (if any)
        // and the remaining aggregate coverage. Recoveries net of deductible.
        if (hasInsurance) {
          const sublimit = resolveSublimit(scenario.scenarioId)
          const effectiveCap = sublimit && sublimit > 0 ? Math.min(sublimit, coverageLimit) : coverageLimit
          const remainingAgg = Math.max(0, coverageLimit - yearlyInsured)
          const incidentInsured = Math.min(
            Math.max(0, loss - deductible),
            Math.max(0, effectiveCap - deductible),
            remainingAgg
          )
          yearlyInsured += incidentInsured
        }
      }
    }

    if (incidentCount === 0) zeroIncidents++
    else if (incidentCount === 1) oneIncident++
    else if (incidentCount === 2) twoIncidents++
    else threeOrMore++

    aggregateGross.push(yearlyGross)

    // Apply insurance — sublimit-aware path uses yearlyInsured already
    // accumulated above; legacy fallback applies aggregate cap when no
    // sublimit data is present.
    let netLoss = yearlyGross
    if (hasInsurance && yearlyGross > 0) {
      if (Object.keys(sublimitMap).length > 0) {
        netLoss = Math.max(0, yearlyGross - yearlyInsured)
      } else {
        const insuredAmount = Math.min(Math.max(0, yearlyGross - deductible), coverageLimit - deductible)
        netLoss = yearlyGross - insuredAmount
      }
    }
    aggregateNet.push(netLoss)
  }

  const grossStats = calculateStats(aggregateGross)
  const netStats = calculateStats(aggregateNet)

  // Exceedance curve — dynamic thresholds, trimmed at last non-zero probability
  const p95Val = grossStats.percentiles.p95 || 2000000
  const maxVal = grossStats.max || p95Val
  const curveMax = Math.max(maxVal, coverageLimit * 1.2 || 0, p95Val * 1.1)
  const rawThresholds = []
  const steps = 20
  for (let i = 0; i <= steps; i++) {
    rawThresholds.push(Math.round((curveMax / steps) * i))
  }
  const rawCurve = rawThresholds.map(t => ({
    threshold: t,
    probability: Math.round((aggregateGross.filter(v => v > t).length / iterations) * 100) / 100
  }))
  // Trim: keep up to one point past the last non-zero probability
  const lastNonZeroIdx = rawCurve.reduce((last, pt, idx) => pt.probability > 0 ? idx : last, 0)
  const exceedanceCurve = rawCurve.slice(0, Math.min(lastNonZeroIdx + 2, rawCurve.length))

  // Insurance utilization
  const insuredIterations = aggregateGross.filter(v => v > deductible).length
  const insuranceUtilizationRate = Math.round((insuredIterations / iterations) * 100) / 100
  const retentionBurns = aggregateGross.filter(v => v > deductible).length
  const retentionBurnRate = iterations > 0 ? Math.round((retentionBurns / iterations) * 100) / 100 : 0

  return {
    iterations,
    aggregateDistribution: grossStats,
    netDistribution: netStats,
    exceedanceCurve,
    incidentFrequency: {
      zeroIncidents: Math.round((zeroIncidents / iterations) * 100) / 100,
      oneIncident: Math.round((oneIncident / iterations) * 100) / 100,
      twoIncidents: Math.round((twoIncidents / iterations) * 100) / 100,
      threeOrMore: Math.round((threeOrMore / iterations) * 100) / 100
    },
    insuranceAnalysis: {
      grossAggregateP90: grossStats.percentiles.p90,
      netAggregateP90: netStats.percentiles.p90,
      insuranceUtilizationRate,
      retentionBurnRate
    },
    executiveSummary: `Based on your current security posture, there is a ${Math.round((1 - zeroIncidents / iterations) * 100)}% probability of experiencing at least one cyber incident annually. The median annual exposure is $${formatCurrency(grossStats.percentiles.p50)}, with a 10% chance of exceeding $${formatCurrency(grossStats.percentiles.p90)}.`
  }
}

// ── Stress Test Controls Map ────────────────────────────────────────────────

export const STRESS_TEST_CONTROLS = {
  'ma-0': { label: 'Multi-factor authentication', category: 'Access & Identity', improvedValue: 'yes', affectsScenarios: ['data_breach', 'financial_theft', 'insider_threat'], description: 'Enforce MFA for all users including service accounts' },
  'ma-22': { label: 'Endpoint detection & response', category: 'Technology', improvedValue: 'yes', affectsScenarios: ['cyber_extortion', 'data_breach'], description: 'Deploy modern EDR with behavioral detection' },
  'ma-23': { label: 'Backup validation testing', category: 'Operations', improvedValue: 'yes', affectsScenarios: ['cyber_extortion', 'business_interruption'], description: 'Regular tested backup recovery with verified restores' },
  'ma-24': { label: 'Risk-based patch management', category: 'Operations', improvedValue: 'yes', affectsScenarios: ['cyber_extortion', 'data_breach', 'third_party'], description: 'Systematic patching prioritized by severity and exposure' },
  'ma-26': { label: 'Centralized logging & monitoring', category: 'Operations', improvedValue: 'yes', affectsScenarios: ['data_breach', 'insider_threat'], description: 'Centralized log aggregation with alerting' },
  'ma-56': { label: 'Network segmentation', category: 'Technology', improvedValue: 'yes', affectsScenarios: ['cyber_extortion', 'data_breach'], description: 'Segmentation between critical, user, and untrusted zones' },
  'ma-59': { label: '24/7 security monitoring', category: 'Operations', improvedValue: 'yes', affectsScenarios: ['data_breach', 'cyber_extortion', 'insider_threat'], description: 'SOC or MDR providing continuous monitoring' },
  'ts-2': { label: 'Modern identity platform', category: 'Technology', improvedValue: 'Microsoft Entra ID (Azure AD)', affectsScenarios: ['data_breach', 'financial_theft'], description: 'Cloud identity provider with conditional access' },
  'ts-4': { label: 'Modern endpoint protection', category: 'Technology', improvedValue: 'Microsoft Defender for Endpoint / Intune', affectsScenarios: ['cyber_extortion', 'data_breach'], description: 'EDR platform with behavioral detection' },
  'ts-5': { label: 'Enterprise backup solution', category: 'Technology', improvedValue: 'Veeam', affectsScenarios: ['cyber_extortion', 'business_interruption'], description: 'Enterprise backup with immutable storage and automated testing' },
  'ts-6': { label: 'Enterprise firewall', category: 'Technology', improvedValue: 'Fortinet (FortiGate)', affectsScenarios: ['cyber_extortion', 'data_breach'], description: 'Business-class firewall with IPS and segmentation' }
}

// Initiative-to-control mappings for before/after comparison
const INITIATIVE_TO_CONTROLS = {
  'mfa-implementation': { 'ma-0': 'yes' },
  'mfa': { 'ma-0': 'yes' },
  'endpoint-protection': { 'ts-4': 'Microsoft Defender for Endpoint / Intune' },
  'endpoint': { 'ts-4': 'Microsoft Defender for Endpoint / Intune' },
  'backup-validation': { 'ma-23': 'yes' },
  'backup': { 'ma-23': 'yes', 'ts-5': 'Veeam' },
  'patch-management': { 'ma-24': 'yes' },
  'patching': { 'ma-24': 'yes' },
  'vulnerability-scanning': { 'ma-24': 'mostly' },
  'network-segmentation': { 'ma-56': 'mostly' },
  'segmentation': { 'ma-56': 'mostly' },
  'siem-soc': { 'ma-59': 'yes', 'ma-26': 'yes' },
  'siem': { 'ma-26': 'yes' },
  'monitoring': { 'ma-59': 'yes', 'ma-26': 'yes' },
  'firewall': { 'ts-6': 'Fortinet (FortiGate)' },
  'identity': { 'ts-2': 'Microsoft Entra ID (Azure AD)' }
}

/**
 * Get all available stress test controls.
 */
export function getStressTestControls() {
  return STRESS_TEST_CONTROLS
}

/**
 * Get controls relevant to a specific scenario.
 */
export function getControlsForScenario(scenarioId) {
  return Object.entries(STRESS_TEST_CONTROLS)
    .filter(([, ctrl]) => ctrl.affectsScenarios.includes(scenarioId))
    .map(([id, ctrl]) => ({ id, ...ctrl }))
}

/**
 * Stress test a single scenario by toggling a control improvement.
 * Runs simulation twice and returns comparison.
 */
export function stressTestScenario(
  scenarioParams,
  currentCalibration,
  controlToToggle,
  improvedValue,
  recalculateCalibration,
  currentMaturityResponses,
  technologyScopingResponses,
  businessScopingResponses,
  iterations = 10000
) {
  const controlDef = STRESS_TEST_CONTROLS[controlToToggle]
  if (!controlDef) return null

  const improved = improvedValue || controlDef.improvedValue
  const scenarioId = scenarioParams.scenarioId

  // Build current calibration for this scenario
  const currentCal = currentCalibration?.scenarioAdjustments?.[scenarioId] || { probabilityModifier: 1, costModifier: 1 }

  // Run current simulation
  const currentResult = simulateScenario({
    ...scenarioParams,
    calibration: currentCal
  }, iterations)

  // Compute improved calibration
  let improvedCal = { ...currentCal }

  if (recalculateCalibration && typeof recalculateCalibration === 'function') {
    // Use provided recalculation function (calculateDynamicCalibration)
    const modifiedMaturity = controlToToggle.startsWith('ma-')
      ? { ...(currentMaturityResponses || {}), [controlToToggle]: improved }
      : (currentMaturityResponses || {})
    const modifiedTech = controlToToggle.startsWith('ts-')
      ? { ...(technologyScopingResponses || {}), [controlToToggle]: { value: improved } }
      : (technologyScopingResponses || {})

    const newCalibration = recalculateCalibration(modifiedMaturity, modifiedTech, [], businessScopingResponses || {})
    improvedCal = newCalibration?.scenarioAdjustments?.[scenarioId] || improvedCal
  } else {
    // Estimate improvement: reduce modifiers toward 1.0 by 30-50% for the toggled control
    const reductionFactor = 0.6
    if (improvedCal.probabilityModifier > 1) {
      improvedCal = { ...improvedCal, probabilityModifier: 1 + (improvedCal.probabilityModifier - 1) * reductionFactor }
    }
    if (improvedCal.costModifier > 1) {
      improvedCal = { ...improvedCal, costModifier: 1 + (improvedCal.costModifier - 1) * reductionFactor }
    }
  }

  // Run improved simulation
  const improvedResult = simulateScenario({
    ...scenarioParams,
    scenarioId: scenarioId + '_improved', // Different seed
    calibration: improvedCal
  }, iterations)

  // Calculate deltas
  const currentP90 = currentResult.distribution.percentiles.p90
  const improvedP90 = improvedResult.distribution.percentiles.p90
  const currentALE = Math.round(currentResult.occurrenceRate * currentResult.distribution.mean)
  const improvedALE = Math.round(improvedResult.occurrenceRate * improvedResult.distribution.mean)

  const grossReduction = Math.max(0, (scenarioParams.grossLoss || 0) * (currentCal.costModifier || 1) - (scenarioParams.grossLoss || 0) * (improvedCal.costModifier || 1))

  return {
    scenarioId,
    scenarioName: scenarioParams.scenarioName || scenarioId,
    controlToggled: {
      id: controlToToggle,
      label: controlDef.label,
      currentValue: controlToToggle.startsWith('ts-')
        ? (technologyScopingResponses?.[controlToToggle]?.value || 'Unknown')
        : (currentMaturityResponses?.[controlToToggle] || 'Unknown'),
      simulatedValue: improved,
      improvement: controlDef.description
    },
    current: {
      probability: currentResult.occurrenceRate,
      grossLoss: currentResult.distribution.percentiles.p75,
      ale: currentALE,
      p50: currentResult.distribution.percentiles.p50,
      p75: currentResult.distribution.percentiles.p75,
      p90: currentP90,
      p95: currentResult.distribution.percentiles.p95
    },
    improved: {
      probability: improvedResult.occurrenceRate,
      grossLoss: improvedResult.distribution.percentiles.p75,
      ale: improvedALE,
      p50: improvedResult.distribution.percentiles.p50,
      p75: improvedResult.distribution.percentiles.p75,
      p90: improvedP90,
      p95: improvedResult.distribution.percentiles.p95
    },
    delta: {
      grossLossReduction: Math.round(grossReduction),
      grossLossReductionPercent: (scenarioParams.grossLoss || 1) > 0 ? Math.round((grossReduction / ((scenarioParams.grossLoss || 1) * (currentCal.costModifier || 1))) * 100) : 0,
      aleReduction: Math.max(0, currentALE - improvedALE),
      aleReductionPercent: currentALE > 0 ? Math.round(((currentALE - improvedALE) / currentALE) * 100) : 0,
      p90Reduction: Math.max(0, currentP90 - improvedP90),
      p90ReductionPercent: currentP90 > 0 ? Math.round(((currentP90 - improvedP90) / currentP90) * 100) : 0
    },
    narrative: `Implementing ${controlDef.label.toLowerCase()} reduces your ${(scenarioParams.scenarioName || scenarioId).toLowerCase()} exposure by approximately ${currentALE > 0 ? Math.round(((currentALE - improvedALE) / currentALE) * 100) : 0}%. The 90th percentile loss drops from $${formatCurrency(currentP90)} to $${formatCurrency(improvedP90)}, reducing annualized risk by $${formatCurrency(Math.max(0, currentALE - improvedALE))}.`
  }
}

/**
 * Compare aggregate risk exposure before and after implementing recommended initiatives.
 */
export function compareBeforeAfter(
  currentScenarioResults,
  currentCalibration,
  impactData,
  currentMaturityResponses,
  currentTechScoping,
  quickWinInitiatives,
  recalculateCalibration,
  businessScopingResponses,
  iterations = 10000
) {
  // Run current aggregate
  const currentAgg = simulateAggregateExposure(currentScenarioResults, currentCalibration, impactData, iterations)

  // Build improved responses by applying all initiative control mappings
  const improvedMaturity = { ...(currentMaturityResponses || {}) }
  const improvedTech = { ...(currentTechScoping || {}) }
  const controlsImproved = []
  const controlsUnchanged = []

  for (const initiative of (quickWinInitiatives || [])) {
    const initKey = (initiative.id || initiative.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

    // Find matching control mappings
    let mapped = false
    for (const [pattern, controls] of Object.entries(INITIATIVE_TO_CONTROLS)) {
      if (initKey.includes(pattern)) {
        for (const [controlId, value] of Object.entries(controls)) {
          if (controlId.startsWith('ma-')) {
            const from = improvedMaturity[controlId] || 'no'
            improvedMaturity[controlId] = value
            controlsImproved.push({ id: controlId, label: STRESS_TEST_CONTROLS[controlId]?.label || controlId, from, to: value })
          } else if (controlId.startsWith('ts-')) {
            const from = improvedTech[controlId]?.value || 'Unknown'
            improvedTech[controlId] = { value }
            controlsImproved.push({ id: controlId, label: STRESS_TEST_CONTROLS[controlId]?.label || controlId, from, to: value })
          }
        }
        mapped = true
        break
      }
    }

    if (!mapped) {
      controlsUnchanged.push({
        initiative: initiative.name || initiative.id || initKey,
        reason: 'Governance or process improvement — not directly modeled in loss scenarios'
      })
    }
  }

  // Recalculate calibration with improved responses
  let improvedCalibration = currentCalibration
  if (recalculateCalibration && typeof recalculateCalibration === 'function') {
    improvedCalibration = recalculateCalibration(improvedMaturity, improvedTech, [], businessScopingResponses || {})
  }

  // Run improved aggregate
  const improvedAgg = simulateAggregateExposure(currentScenarioResults, improvedCalibration, impactData, iterations)

  // Calculate investment context
  const quickWinCost = (quickWinInitiatives || []).reduce((sum, i) => {
    const cost = i.estimatedCost || i.cost || 0
    return { min: sum.min + (typeof cost === 'object' ? cost.min || 0 : cost * 0.7), max: sum.max + (typeof cost === 'object' ? cost.max || 0 : cost * 1.3) }
  }, { min: 0, max: 0 })

  const aleReduction = Math.max(0, currentAgg.aggregateDistribution.mean - improvedAgg.aggregateDistribution.mean)
  const avgCost = (quickWinCost.min + quickWinCost.max) / 2
  const paybackMonths = aleReduction > 0 ? Math.round((avgCost / aleReduction) * 12) : 0
  const threeYearNet = Math.round(aleReduction * 3 - avgCost)

  const currentP90 = currentAgg.aggregateDistribution.percentiles.p90
  const improvedP90 = improvedAgg.aggregateDistribution.percentiles.p90
  const currentP95 = currentAgg.aggregateDistribution.percentiles.p95
  const improvedP95 = improvedAgg.aggregateDistribution.percentiles.p95

  return {
    current: {
      aggregateALE: currentAgg.aggregateDistribution.mean,
      p50: currentAgg.aggregateDistribution.percentiles.p50,
      p75: currentAgg.aggregateDistribution.percentiles.p75,
      p90: currentP90,
      p95: currentP95,
      incidentProbability: 1 - currentAgg.incidentFrequency.zeroIncidents
    },
    improved: {
      aggregateALE: improvedAgg.aggregateDistribution.mean,
      p50: improvedAgg.aggregateDistribution.percentiles.p50,
      p75: improvedAgg.aggregateDistribution.percentiles.p75,
      p90: improvedP90,
      p95: improvedP95,
      incidentProbability: 1 - improvedAgg.incidentFrequency.zeroIncidents
    },
    delta: {
      aleReduction: Math.round(aleReduction),
      aleReductionPercent: currentAgg.aggregateDistribution.mean > 0 ? Math.round((aleReduction / currentAgg.aggregateDistribution.mean) * 100) : 0,
      p90Reduction: Math.max(0, currentP90 - improvedP90),
      p90ReductionPercent: currentP90 > 0 ? Math.round(((currentP90 - improvedP90) / currentP90) * 100) : 0,
      p95Reduction: Math.max(0, currentP95 - improvedP95),
      p95ReductionPercent: currentP95 > 0 ? Math.round(((currentP95 - improvedP95) / currentP95) * 100) : 0,
      incidentProbabilityReduction: Math.round(((1 - currentAgg.incidentFrequency.zeroIncidents) - (1 - improvedAgg.incidentFrequency.zeroIncidents)) * 100) / 100
    },
    controlsImproved: [...new Map(controlsImproved.map(c => [c.id, c])).values()], // Deduplicate
    controlsUnchanged,
    investmentContext: {
      quickWinCost: { min: Math.round(quickWinCost.min), max: Math.round(quickWinCost.max) },
      annualRiskReduction: Math.round(aleReduction),
      paybackMonths,
      threeYearNetBenefit: threeYearNet
    },
    narrative: `Implementing the ${(quickWinInitiatives || []).length} recommended quick-win initiatives reduces your 90th percentile annual exposure from $${formatCurrency(currentP90)} to $${formatCurrency(improvedP90)} — a ${currentP90 > 0 ? Math.round(((currentP90 - improvedP90) / currentP90) * 100) : 0}% reduction. The estimated investment of $${formatCurrency(quickWinCost.min)}-$${formatCurrency(quickWinCost.max)} pays back within ${paybackMonths} months through reduced risk exposure. Note: governance and awareness initiatives provide additional risk reduction not captured in this simulation.`
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function hashString(str) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return Math.abs(hash) || 42
}

function formatCurrency(value) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${Math.round(value / 1000)}K`
  return `${Math.round(value)}`
}
